import React, { useEffect, useState } from "react";
import { api } from "../api";

const STATUS_STYLE = {
  completed: { label: "✓", className: "bg-green-100 text-green-700" },
  in_progress: { label: "…", className: "bg-yellow-100 text-yellow-700" },
  not_started: { label: "—", className: "bg-gray-100 text-gray-400" },
  no_content: { label: "n/a", className: "bg-gray-50 text-gray-300" },
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.dashboard().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!data) return <div className="text-gray-500">Loading…</div>;

  const exportCsv = () => {
    const rows = [["Caregiver", "Email", "Overall %", "Overdue Count", ...data.courses.map((c) => c.title)]];
    data.matrix.forEach((row) => {
      rows.push([
        row.caregiver.name,
        row.caregiver.email,
        row.overall_percent,
        row.overdue_count,
        ...row.courses.map((c) => (c.is_overdue ? "overdue" : c.status.replace("_", " "))),
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Compliance Dashboard</h1>
          <p className="text-sm text-gray-500">
            {data.summary.total_caregivers} caregivers · {data.summary.total_courses} published courses · {data.summary.completion_rate}% overall completion
          </p>
        </div>
        <button onClick={exportCsv} className="px-4 py-2 rounded text-sm font-medium border" style={{ borderColor: "#154E93", color: "#154E93" }}>
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="text-sm">
          <thead style={{ backgroundColor: "#154E93" }}>
            <tr className="text-white text-left">
              <th className="px-4 py-2 sticky left-0" style={{ backgroundColor: "#154E93" }}>Caregiver</th>
              <th className="px-4 py-2 whitespace-nowrap">Overall %</th>
              {data.courses.map((c) => (
                <th key={c.id} className="px-4 py-2 whitespace-nowrap">
                  {c.title}
                  {(c.state_tags || "").split(",").filter(Boolean).map((tag) => (
                    <span key={tag} className="ml-1 inline-block bg-white/20 text-xs px-1 rounded">{tag}</span>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row) => (
              <tr key={row.caregiver.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium bg-white sticky left-0">
                  {row.caregiver.name}
                  <div className="text-xs text-gray-400">{row.caregiver.email}</div>
                </td>
                <td className="px-4 py-2 text-center">
                  <span className={`font-semibold ${row.overall_percent === 100 ? "text-green-600" : row.overall_percent === 0 ? "text-gray-400" : "text-yellow-600"}`}>
                    {row.overall_percent}%
                  </span>
                  {row.overdue_count > 0 && (
                    <div className="text-xs text-red-600 font-medium">{row.overdue_count} overdue</div>
                  )}
                </td>
                {row.courses.map((c) => {
                  const style = STATUS_STYLE[c.status] || STATUS_STYLE.not_started;
                  return (
                    <td key={c.course_id} className="px-4 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.is_overdue ? "bg-red-100 text-red-700" : style.className}`}>
                        {c.is_overdue ? "⚠ overdue" : style.label} {c.total ? `${c.completed}/${c.total}` : ""}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {data.matrix.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={data.courses.length + 2}>No caregivers added yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
